import { Location } from 'history';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, ReactNode, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { IOverlayLoaderStore } from '../../../../modules/common/OverlayLoader.store';
import { IProjectOverviewBl } from '../../../../modules/projects/project/overview/projectOverview.bl';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { EnableFor } from '../../../containers/EnableForRole';
import { useLoader } from '../../../helpers/useLoader';
import { Home } from '../../../routes/config/Home';
import { useProjectPermissions } from '../../projects/projects.permissions';
import { useProjectsService } from '../../projects/projects.service';
import { IUserService } from '../../user/user.service';
import { ProjectPublishConfirmationModal } from '../components/ProjectPublishConfirmationModal';
import { PublishProjectNavigateAwayConfirmationModal } from '../components/PublishProjectNavigateAwayConfirmationModal';
import { IProjectDetailsBl } from '../projectDetails.bl';
import { ProjectPublishStrategy } from '../projectDetails.models';

interface IProps {
  projectId?: string;
  disabled?: boolean;
  children(childrenProps: IPublishButtonProps): ReactNode;
}

export interface IPublishButtonProps {
  disabled?: boolean;
  onClick: () => Promise<void>;
  projectId?: string;
}

export const PublishButtonContainer = observer((props: IProps): ReactElement => {
  const projectsPermissions = useProjectPermissions();

  const projectsService = useProjectsService();
  const projectDetailsBl = useInjection(IProjectDetailsBl.$);
  const projectOverviewBl = useInjection(IProjectOverviewBl.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const userService = useInjection(IUserService.$);
  const overlayLoaderStore = useInjection(IOverlayLoaderStore.$);
  const [showNavOutModal, setShowNavOutModal] = useState(false);
  const [navigationLocation, setNavigationLocation] = useState<Location | string | null>(null);

  const history = useHistory();
  const { isLoading, withLoaderAsync } = useLoader();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [strategy, setStrategy] = useState<ProjectPublishStrategy>(ProjectPublishStrategy.DiscardDrafts);

  const handlePublishConfirmAsync = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    if (props.projectId) {
      const projectId = props.projectId;
      const passedProjectResult = await withLoaderAsync(async (): Promise<boolean> => {
        return await projectDetailsBl.publishAsync(projectId, strategy === ProjectPublishStrategy.DiscardDrafts);
      }, `publish-button-loader-${props.projectId}`);
      if (passedProjectResult) {
        setShowModal(false);
        await projectsService.refreshAsync();
      }
    } else if (projectDetailsBl.store.id) {
      const location = Home.Projects.List.All.withParams({ workspaceId: currentWorkspaceStore.currentWorkspace.id });
      setNavigationLocation(location);
      const projectFromStoreResult = await withLoaderAsync(async (): Promise<boolean> => {
        return await projectDetailsBl.publishAsync(projectDetailsBl.store.id, strategy === ProjectPublishStrategy.DiscardDrafts);
      }, 'publish-button-loader');
      if (projectFromStoreResult) {
        history.push(location);
      }
    }
    await userService.getUserInfoAsync(); // to get user role in project
  };

  const handlePublishCancel = (): void => {
    setShowModal(false);
  };

  const handleOpenModal = async (): Promise<void> => {
    if (!props.projectId) {
      const canPublish = await projectDetailsBl.canPublishAsync();
      if (canPublish) {
        await projectOverviewBl.getProjectDetailsAnnotatingAsync(projectDetailsBl.store.id);
        setShowModal(true);
      }
    } else {
      await projectOverviewBl.getProjectDetailsAnnotatingAsync(props.projectId);
      setShowModal(true);
    }
  };

  const handleStrategyChange = (strategy: ProjectPublishStrategy): void => {
    setStrategy(strategy);
  };

  const handleBlockedNavigation = (location: Location): boolean => {
    if (location.pathname.includes('/projects/') && !location.pathname.includes('/projects/list/all')) {
      return true;
    }

    setNavigationLocation(location);
    projectDetailsBl
      .canPublishAsync()
      .then((canPublish) => {
        if (canPublish) {
          setShowNavOutModal(true);
        } else {
          history.push(location);
        }
      })
      .catch(() => {});
    return false;
  };

  const handleConfirm = async (): Promise<void> => {
    const canPublish = await projectDetailsBl.canPublishAsync();
    if (canPublish) {
      await handlePublishConfirmAsync();
      if (navigationLocation) {
        history.push(navigationLocation);
      }
    }
    setShowNavOutModal(false);
  };

  const handleKeepDraft = (): void => {
    setShowNavOutModal(false);
    if (navigationLocation) {
      history.push(navigationLocation);
    }
  };

  const handleCancel = (): void => {
    setNavigationLocation(null);
    setShowNavOutModal(false);
  };

  return (
    <EnableFor hasPermission={projectsPermissions.canPublish()}>
      <PublishProjectNavigateAwayConfirmationModal
        intercept={!showNavOutModal && !props.projectId && !navigationLocation}
        onBlockedNavigation={handleBlockedNavigation}
        showModal={showNavOutModal}
        onConfirm={handleConfirm}
        onKeepDraft={handleKeepDraft}
        onCancel={handleCancel}
        loaderKey='publish-button-loader'
      />
      {props.children({
        onClick: handleOpenModal,
        disabled: props.disabled || overlayLoaderStore.isSpinnerVisible('publish-loader'),
        projectId: props.projectId,
      })}
      <ProjectPublishConfirmationModal
        showModal={showModal}
        disablePublishButton={props.disabled}
        onConfirm={handlePublishConfirmAsync}
        onCancel={handlePublishCancel}
        projectId={props.projectId}
        draftsCount={projectOverviewBl.store.draftsCount}
        onStrategyChange={handleStrategyChange}
        strategy={strategy}
        hasImportedAnnotations={projectDetailsBl.store.hasImportedAnnotations}
      />
    </EnableFor>
  );
});
